import { useState, useContext, useEffect } from "react";
import axios from "axios";
import { HelpCircle, Heart, MessageCircle, DollarSign, Coins, Send, Crown, Lock } from "lucide-react";
import { toast } from "sonner";
import { API, AuthContext } from "../App";

const QUESTION_CATEGORIES = [
  { id: "life", name: "🌟 Life Advice", icon: "🌟", description: "General life guidance and wisdom" },
  { id: "love", name: "💕 Love & Relationships", icon: "💕", description: "Romance, dating, and relationships" },
  { id: "sex", name: "🔥 Intimacy", icon: "🔥", description: "Physical intimacy questions" },
  { id: "choices", name: "🤔 Good or Bad Choices", icon: "🤔", description: "Help with decisions" },
];

const ENTRY_FEE = 50; // F-Chips to enter
const QUESTION_FEE = 25; // F-Chips per question

const AskYanaPanel = ({ roomId, wsRef, currentUser }) => {
  const { token, user } = useContext(AuthContext);
  const [hasPaidEntry, setHasPaidEntry] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [question, setQuestion] = useState("");
  const [fchipsBalance, setFchipsBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [questionHistory, setQuestionHistory] = useState([]);
  const [yanaOnline, setYanaOnline] = useState(false);
  const [yanaUsername, setYanaUsername] = useState("");

  const isAskYanaRoom = roomId === "ask-yana-room";

  // Fetch F-Chips balance
  useEffect(() => {
    if (!isAskYanaRoom) return;
    const fetchBalance = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/fchips/balance`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFchipsBalance(response.data.balance || 0);
      } catch (error) {
        console.error("Failed to fetch balance:", error);
      }
    };
    fetchBalance();
  }, [token, isAskYanaRoom]);

  // Check if user has paid entry and Yana's status
  useEffect(() => {
    if (!isAskYanaRoom) return;
    const checkEntry = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/ask-yana/entry-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHasPaidEntry(response.data.has_paid || false);
        setYanaOnline(response.data.yana_online || false);
        setYanaUsername(response.data.yana_username || "Yana");
      } catch (error) {
        setHasPaidEntry(false);
      }
    };
    checkEntry();
    // Poll for Yana's online status every 30 seconds
    const interval = setInterval(checkEntry, 30000);
    return () => clearInterval(interval);
  }, [token, isAskYanaRoom]);

  // Only render in ASK YANA room
  if (!isAskYanaRoom) return null;

  const payEntryFee = async () => {
    if (fchipsBalance < ENTRY_FEE) {
      toast.error(`You need ${ENTRY_FEE} F-Chips to enter. You have ${fchipsBalance}.`);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/ask-yana/pay-entry`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || "Entry fee paid! Welcome!");
      setHasPaidEntry(true);
      setFchipsBalance(prev => prev - ENTRY_FEE);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to pay entry fee");
    } finally {
      setLoading(false);
    }
  };

  const submitQuestion = async () => {
    if (!selectedCategory) {
      toast.error("Please select a category");
      return;
    }
    if (!question.trim()) {
      toast.error("Please enter your question");
      return;
    }
    if (fchipsBalance < QUESTION_FEE) {
      toast.error(`You need ${QUESTION_FEE} F-Chips to ask a question. You have ${fchipsBalance}.`);
      return;
    }

    setSubmitting(true);
    try {
      // Deduct F-Chips for question
      const response = await axios.post(`${API}/ask-yana/submit-question`, {
        category: selectedCategory,
        question: question.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Send question to chat
      if (wsRef?.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: "chat_message",
          content: `❓ **[ASK YANA - ${QUESTION_CATEGORIES.find(c => c.id === selectedCategory)?.name}]**\n💬 ${question}\n👤 Asked by: ${currentUser}`,
          is_system: false
        }));
      }

      toast.success("Question submitted! Yana will respond soon. 💕");
      setFchipsBalance(prev => prev - QUESTION_FEE);
      setQuestion("");
      setSelectedCategory(null);
      setQuestionHistory(prev => [...prev, { category: selectedCategory, question, timestamp: new Date() }]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit question");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="retro-window mb-2" data-testid="ask-yana-panel">
      {/* Header */}
      <div className="retro-title-bar px-2 py-1 flex items-center gap-2 bg-gradient-to-r from-pink-500 to-purple-500">
        <span>❓ ASK YANA - Life, Love & More</span>
      </div>

      <div className="p-3 bg-gradient-to-b from-pink-50 to-purple-50">
        {/* YANA IS ONLINE Light */}
        <div className={`mb-3 p-2 rounded text-center ${yanaOnline ? 'bg-green-100 border-2 border-green-400' : 'bg-gray-100 border-2 border-gray-300'}`}>
          <div className="flex items-center justify-center gap-2">
            <div className={`w-3 h-3 rounded-full ${yanaOnline ? 'bg-green-500 animate-pulse shadow-lg shadow-green-500/50' : 'bg-gray-400'}`} />
            <span className={`font-bold text-sm ${yanaOnline ? 'text-green-700' : 'text-gray-500'}`}>
              {yanaOnline ? `🟢 ${yanaUsername || 'YANA'} IS ONLINE!` : '🔴 YANA IS OFFLINE'}
            </span>
          </div>
          {!yanaOnline && (
            <p className="text-[10px] text-gray-500 mt-1">Questions will be answered when Yana comes online</p>
          )}
        </div>

        {/* Intro */}
        <div className="text-center mb-3">
          <Crown className="w-8 h-8 mx-auto text-yellow-500 mb-1" />
          <h3 className="font-bold text-sm text-purple-700">Yana's Advice Corner</h3>
          <p className="text-xs text-gray-600">
            Get advice on Life, Sex, Love, Relationships & Choices
          </p>
        </div>

        {/* F-Chips Balance */}
        <div className="retro-inset p-2 mb-3 bg-white flex items-center justify-between">
          <span className="text-xs flex items-center gap-1">
            <Coins className="w-4 h-4 text-green-600" /> Your F-Chips:
          </span>
          <span className="font-bold text-green-700">{fchipsBalance.toLocaleString()}</span>
        </div>

        {/* Entry Gate */}
        {!hasPaidEntry ? (
          <div className="retro-window p-4 bg-yellow-50 text-center">
            <Lock className="w-10 h-10 mx-auto text-yellow-600 mb-2" />
            <h4 className="font-bold text-sm mb-2">Entry Fee Required</h4>
            <p className="text-xs text-gray-600 mb-3">
              Pay <span className="font-bold text-green-700">{ENTRY_FEE} F-Chips</span> to access Yana's advice room
            </p>
            <button
              className="retro-button px-4 py-2 bg-gradient-to-r from-pink-400 to-purple-400 text-white font-bold"
              onClick={payEntryFee}
              disabled={loading || fchipsBalance < ENTRY_FEE}
            >
              {loading ? "Processing..." : `Pay ${ENTRY_FEE} F-Chips to Enter`}
            </button>
            {fchipsBalance < ENTRY_FEE && (
              <p className="text-[10px] text-red-500 mt-2">
                Not enough F-Chips! Earn more by playing games or trivia.
              </p>
            )}
          </div>
        ) : (
          <>
            {/* Category Selection */}
            <div className="mb-3">
              <p className="text-xs font-bold mb-2">Select a Category:</p>
              <div className="grid grid-cols-2 gap-2">
                {QUESTION_CATEGORIES.map(cat => (
                  <button
                    key={cat.id}
                    className={`retro-button p-2 text-left ${
                      selectedCategory === cat.id 
                        ? 'bg-purple-200 border-purple-500' 
                        : 'hover:bg-pink-100'
                    }`}
                    onClick={() => setSelectedCategory(cat.id)}
                  >
                    <span className="text-lg">{cat.icon}</span>
                    <span className="text-xs font-bold block">{cat.name}</span>
                    <span className="text-[9px] text-gray-500">{cat.description}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Question Input */}
            <div className="mb-3">
              <p className="text-xs font-bold mb-1">Your Question:</p>
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                className="retro-input w-full h-20 text-sm resize-none"
                placeholder="Type your question for Yana..."
                maxLength={500}
              />
              <div className="flex justify-between text-[10px] text-gray-500">
                <span>{question.length}/500 characters</span>
                <span>Cost: {QUESTION_FEE} F-Chips per question</span>
              </div>
            </div>

            {/* Submit Button */}
            <button
              className="retro-button w-full py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white font-bold flex items-center justify-center gap-2"
              onClick={submitQuestion}
              disabled={submitting || !selectedCategory || !question.trim() || fchipsBalance < QUESTION_FEE}
            >
              <Send className="w-4 h-4" />
              {submitting ? "Submitting..." : `Ask Yana (${QUESTION_FEE} F-Chips)`}
            </button>

            {/* Question History */}
            {questionHistory.length > 0 && (
              <div className="mt-3 retro-inset p-2 bg-white">
                <p className="text-[10px] font-bold text-gray-600 mb-1">Your Questions Today:</p>
                <div className="max-h-20 overflow-y-auto retro-scrollbar">
                  {questionHistory.map((q, i) => (
                    <div key={i} className="text-[10px] text-gray-500 mb-1">
                      {QUESTION_CATEGORIES.find(c => c.id === q.category)?.icon} {q.question.substring(0, 50)}...
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tips */}
            <div className="mt-3 text-[9px] text-center text-gray-500">
              💡 Yana checks questions regularly. Be patient for your answer!
              <br />
              ❤️ All advice is given with love and respect.
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AskYanaPanel;
